import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import type { LibraryElement } from './LibraryElement';

const TOOLTIP_DELAY_MS = 600;

interface Props {
    element: LibraryElement;
    selected: boolean;
    onHover: () => void;
    onClick: () => void;
    scrollIntoView?: boolean;
}

export const NodeLibraryNodeItem: React.FC<Props> = ({ element, selected, onHover, onClick, scrollIntoView }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (selected && scrollIntoView && itemRef.current) {
            itemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selected, scrollIntoView]);

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setShowTooltip(false);

        if (selected && element.description) {
            timerRef.current = setTimeout(() => setShowTooltip(true), TOOLTIP_DELAY_MS);
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [selected, element]);

    return (
        <div
            ref={itemRef}
            className={cn('relative px-3 py-1.5 cursor-pointer text-sm text-foreground transition-colors', selected && 'font-medium')}
            style={
                selected
                    ? { backgroundColor: `${element.color}30`, borderLeft: `2px solid ${element.color}` }
                    : { borderLeft: '2px solid transparent' }
            }
            onMouseMove={onHover}
            onClick={onClick}
        >
            {element.name}
            {showTooltip &&
                itemRef.current &&
                createPortal(
                    <div
                        className="fixed z-50 w-[260px] px-3 py-2.5 rounded-lg backdrop-blur-xl bg-white/80 dark:bg-black/60 border border-black/10 dark:border-white/10 shadow-lg animate-in fade-in slide-in-from-right-1 duration-150 pointer-events-none"
                        style={{
                            top: itemRef.current.getBoundingClientRect().top,
                            right: window.innerWidth - itemRef.current.getBoundingClientRect().left + 8,
                            borderLeft: `3px solid ${element.color}99`,
                        }}
                    >
                        <div className="text-sm font-semibold text-foreground mb-1">{element.name}</div>
                        <div className="text-xs text-muted-foreground leading-relaxed">{element.description}</div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};
